/* eslint-disable import/no-extraneous-dependencies */
import React, {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import api from 'api';
import useUser from 'hooks/useUser';
import useRequest from 'hooks/useRequest';
import { Link, useNavigate } from 'react-router-dom';
import { Container, Row, Col } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUsers,
  faChartBar,
  faEnvelope,
  faAmbulance,
  faNotesMedical,
  faUpload,
  faBed,
} from '@fortawesome/free-solid-svg-icons';
import { appRoles } from 'utils/enums';
import { UserType } from 'types/user';
import ContactAdminModal from 'components/modals/ContactAdminModal';
import AnnouncementBanner from 'components/announcements/AnnouncementBanner';
import { MainRoutes } from 'helpers/routes';
import {
  DashboardCard,
  DashboardCardIcon,
  DashboardContent,
  DashboardItemCenter,
  DashboardItemCenterInfo,
  DashboardItemIcon,
  DashboardItemTitle,
} from './HomePage.styles';

type LinkItem = {
  iconClass: any;
  href?: string;
  to?: string;
  label: string;
  roles?: string[];
  conditions?: string[];
  permissions?: string[];
  sameWindow?: boolean;
};
type ListDataResponse = {
  data: UserType[];
  count: number;
  page: number;
  pageSize: number;
  pageCount: number;
};
const { REACT_APP_DATA_ENTRY_URL } = process.env;

const LEFT_LINKS: LinkItem[] = [
  {
    iconClass: faBed,
    to: MainRoutes.BED_BOARD,
    label: 'Bed Board Input',
    permissions: ['bed_entry'],
    conditions: ['hasBurnCenterId'],
  },
  {
    iconClass: faUsers,
    href: REACT_APP_DATA_ENTRY_URL,
    label: 'Enter or edit burn patient data',
    sameWindow: true,
    permissions: ['data_entry_access'],
    roles: [appRoles.bdataADMIN, appRoles.manager,
      appRoles.burnCenterUser, appRoles.abaADMIN],
  },
  {
    iconClass: faChartBar,
    to: '/reports',
    label: 'View or create reports',
    permissions: ['report_access'],
    roles: [appRoles.bdataADMIN, appRoles.manager,
      appRoles.abaADMIN, appRoles.burnCenterUser],
  },
  {
    iconClass: faAmbulance,
    to: '/burn-centers',
    label: 'See burn center profiles',
    conditions: ['withoutBurnCenterId'],
    roles: [appRoles.bdataADMIN, appRoles.abaADMIN, appRoles.burnCenterUser,
      appRoles.abaADMIN],
  },
  {
    iconClass: faAmbulance,
    to: '/profile',
    label: 'Update your burn center profile',
    conditions: ['hasBurnCenterId'],
    roles: [appRoles.burnCenterUser, appRoles.manager, appRoles.abaADMIN],
  },
  {
    iconClass: faNotesMedical,
    to: '/verifications',
    label: 'Verification',
    permissions: ['verification_access'],
    roles: [appRoles.bdataADMIN, appRoles.manager,
      appRoles.burnCenterUser, appRoles.abaADMIN],
  },
];

type NameLike = {
  name?: string | null;
  nickname?: string | null;
  email?: string | null;
};

const pickName = (u: NameLike) => (u?.name?.trim() || u?.nickname?.trim() || u?.email || '');

const Dashboard = () => {
  const {
    appUser, roleName, hasReports, hasVerification, hasBedEntry,
    hasClinicalStudioRole, isSiteReviewer,
  } = useUser();

  const navigate = useNavigate();
  const [showAdminModal, setShowAdminModal] = useState(false);
  const enableModal = (roleName === appRoles.burnCenterUser
    || roleName === appRoles.manager || isSiteReviewer);

  const userPermissions = useMemo(() => {
    const perms = new Set<string>();
    if (hasReports) perms.add('report_access');
    if (hasVerification) perms.add('verification_access');
    if (hasClinicalStudioRole) perms.add('data_entry_access');
    if (hasBedEntry) perms.add('bed_entry');
    return perms;
  }, [hasReports, hasVerification, hasClinicalStudioRole, hasBedEntry]);

  const canSee = useCallback(
    (item: LinkItem) => {
      const roleOk = !item.roles
        || item.roles.includes(roleName)
        || isSiteReviewer;
      const permsOk = !item.permissions || item.permissions.every((p) => userPermissions.has(p));
      const blnRoleOk = roleOk && permsOk;
      if (item.conditions) {
        if (item.conditions.includes('hasBurnCenterId')) {
          return blnRoleOk && appUser?.burnCenterId;
        }
        if (item.conditions.includes('withoutBurnCenterId')) {
          return blnRoleOk && !appUser?.burnCenterId;
        }
      }

      return blnRoleOk;
    },
    [roleName, userPermissions, appUser, isSiteReviewer],
  );

  const visibleLinks = useMemo(
    () => LEFT_LINKS.filter(canSee),
    [canSee],
  );

  const {
    doRequest: getListData,
    data: listData,
  } = useRequest<ListDataResponse>(api.getMyAdmins);

  const fetchListData = useCallback(() => {
    if (enableModal) getListData({});
  }, [getListData, enableModal]);

  useEffect(() => {
    if (enableModal) fetchListData();
  }, [fetchListData, enableModal]);

  const admins = useMemo(
    () => (listData?.data ?? []).map((u) => ({
      name: pickName(u),
      email: u.email,
    })),
    [listData],
  );

  return (
    <Container className="my-5 site-content">
      <div className="d-md-none mb-4">
        <AnnouncementBanner className="announcement-banner--home" />
      </div>
      <Row>
        <Col md={8} className="dashboard-links">
          {visibleLinks.map(({
            iconClass, href, label, to, sameWindow,
          }) => (
            <div className="d-flex align-items-center mb-4" key={href || to}>
              <DashboardItemIcon className="me-4 px-3 py-2">
                <FontAwesomeIcon icon={iconClass} />
              </DashboardItemIcon>
              <DashboardItemTitle className="fw-normal mb-0">
                {to ? (
                  <Link to={to}>{label}</Link>
                ) : (
                  <a href={href} target={sameWindow ? '_self' : '_blank'} rel="noopener noreferrer">
                    {label}
                  </a>
                )}
              </DashboardItemTitle>
            </div>
          ))}
        </Col>

        <Col md={4}>
          {enableModal && (
            <DashboardCard
              onClick={(e) => {
                e.preventDefault();
                setShowAdminModal(true);
              }}
              className="mb-3 contact-admin-card"
            >
              <DashboardCardIcon className="me-4">
                <FontAwesomeIcon icon={faEnvelope} />
              </DashboardCardIcon>

              <DashboardItemCenter>
                <h5 className="fw-normal mb-1">
                  Contact your
                  {' '}
                  <br />
                  Burn Center Admin
                </h5>
                <DashboardItemCenterInfo className="fst-italic mb-0">
                  Need help with your account?
                </DashboardItemCenterInfo>
              </DashboardItemCenter>
            </DashboardCard>
          )}

          <DashboardCard
            onClick={(e) => {
              e.preventDefault();
              navigate('/data_uploaders');
            }}
            className="mb-3"
          >
            <DashboardCardIcon>
              <FontAwesomeIcon icon={faUpload} />
            </DashboardCardIcon>
            <h5 className="px3 w-100 mb-0 text-center fw-normal">
              Data Uploader
            </h5>
          </DashboardCard>

          <DashboardContent className="card card-shadow p-4 mb-3">
            <a
              target="_blank"
              rel="noopener noreferrer"
              href="https://www.ameriburn.org/quality-care"
            >
              ABA Registry Program
            </a>
            <p className="mb-5">
              Learn more about the ABA’s registry program, BCQP participation,
              research data sets, and more.
            </p>

            <a
              target="_blank"
              rel="noopener noreferrer"
              href="https://my.ameriburn.org/home"
            >
              Quality &amp; Registry Community
            </a>
            <p className="mb-5">
              Join the registry community in myAmeriburn. Network, share
              resources, and learn about upcoming data dictionary and registry
              events.
            </p>

            <a
              target="_blank"
              rel="noopener noreferrer"
              href="http://ameriburn.org/quality-care/verification"
            >
              Verification Program
            </a>
          </DashboardContent>
        </Col>
      </Row>
      {enableModal && (
        <ContactAdminModal
          show={showAdminModal}
          onHide={() => setShowAdminModal(false)}
          admins={admins}
        />
      )}
    </Container>
  );
};

export default Dashboard;
